import { useState } from "react";
import { Sparkles, Loader2, Star, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Recommendation {
  product_id: string;
  name: string;
  price: number;
  type: string;
  reason: string;
  match_score: number;
}

const PhoneRecommendation = () => {
  const [step, setStep] = useState(0);
  const [budget, setBudget] = useState("");
  const [usage, setUsage] = useState("");
  const [priority, setPriority] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<{ recommendations: Recommendation[]; summary: string } | null>(null);
  const [error, setError] = useState("");

  const usageOptions = ["Redes sociais", "Jogos", "Trabalho", "Fotos e vídeos", "Uso geral"];
  const priorityOptions = ["Melhor custo-benefício", "Melhor câmera", "Melhor bateria", "Melhor desempenho", "Menor preço"];

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: fnError } = await supabase.functions.invoke("recommend-phone", {
        body: { budget, usage, priority },
      });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      setResults(data.data);
      setStep(4);
    } catch (e: any) {
      setError(e.message || "Erro ao buscar recomendações");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  if (step === 0) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6 text-center">
        <Sparkles className="w-10 h-10 text-primary mx-auto mb-3" />
        <h3 className="text-lg font-bold text-foreground mb-2">Recomendação por IA</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Responda 3 perguntas rápidas e nossa IA sugere o celular ideal para você!
        </p>
        <button
          onClick={() => setStep(1)}
          className="bg-primary text-primary-foreground px-6 py-2 rounded-xl font-medium hover:opacity-90 transition-opacity"
        >
          Começar
        </button>
      </div>
    );
  }

  if (step === 4 && results) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-bold text-foreground">Recomendações para Você</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{results.summary}</p>
        <div className="space-y-3">
          {results.recommendations.map((rec, i) => (
            <div key={i} className="bg-secondary/50 border border-border rounded-xl p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full font-medium">
                    #{i + 1} • {rec.type === "novo" ? "Novo" : "Seminovo"}
                  </span>
                  <h4 className="text-foreground font-semibold mt-1">{rec.name}</h4>
                  <p className="text-primary font-bold">{formatCurrency(rec.price)}</p>
                </div>
                <div className="flex items-center gap-1 bg-primary/10 px-2 py-1 rounded-lg">
                  <Star className="w-3.5 h-3.5 text-primary fill-primary" />
                  <span className="text-sm font-bold text-primary">{rec.match_score}</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{rec.reason}</p>
            </div>
          ))}
        </div>
        <button
          onClick={() => { setStep(0); setResults(null); setBudget(""); setUsage(""); setPriority(""); }}
          className="mt-4 text-sm text-primary hover:underline"
        >
          Buscar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-primary" />
        <h3 className="text-sm font-bold text-foreground">
          Pergunta {step} de 3
        </h3>
      </div>

      {step === 1 && (
        <div>
          <p className="text-foreground font-medium mb-3">Qual seu orçamento?</p>
          <div className="grid grid-cols-2 gap-2">
            {["Até R$ 1.000", "R$ 1.000 - R$ 2.000", "R$ 2.000 - R$ 3.500", "Acima de R$ 3.500"].map((opt) => (
              <button
                key={opt}
                onClick={() => { setBudget(opt); setStep(2); }}
                className={`p-3 rounded-xl border text-sm text-left transition-all ${
                  budget === opt ? "border-primary bg-primary/10 text-primary" : "border-border text-foreground hover:border-primary/50"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <p className="text-foreground font-medium mb-3">Para que você mais usa o celular?</p>
          <div className="grid grid-cols-2 gap-2">
            {usageOptions.map((opt) => (
              <button
                key={opt}
                onClick={() => { setUsage(opt); setStep(3); }}
                className={`p-3 rounded-xl border text-sm text-left transition-all ${
                  usage === opt ? "border-primary bg-primary/10 text-primary" : "border-border text-foreground hover:border-primary/50"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <p className="text-foreground font-medium mb-3">Qual sua prioridade?</p>
          <div className="grid grid-cols-2 gap-2">
            {priorityOptions.map((opt) => (
              <button
                key={opt}
                onClick={() => { setPriority(opt); }}
                className={`p-3 rounded-xl border text-sm text-left transition-all ${
                  priority === opt ? "border-primary bg-primary/10 text-primary" : "border-border text-foreground hover:border-primary/50"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
          {priority && (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="mt-4 w-full bg-primary text-primary-foreground py-2.5 rounded-xl font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analisando...</> : <>Ver recomendações <ChevronRight className="w-4 h-4" /></>}
            </button>
          )}
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default PhoneRecommendation;
